import { Dropdown, Icon } from '@wyz/ui';
import { PrivateComponent } from '~/common/components';
import { Trans } from '~/common/context';
import { ROLES } from '~/common/helpers';
import { DESTOCKING_POLICY_STATUS } from '~/purchase/schemas';
import { IDestockingPricingPolicy } from '~/purchase/types';

interface Props {
  policy: IDestockingPricingPolicy;
  onDownload: () => void;
  onClose: () => void;
}

/**
 * Row-level ⋮ menu holding the actions of a destocking operation — downloading the file it was
 * imported from, and closing it.
 *
 * Each action carries its own role guard. Closing stays rendered once the operation is closed, but
 * disabled: an action that disappears reads as a missing permission, a greyed-out one reads as
 * "already done". Downloading is guarded by GET — the `/{id}/file` route is, too, and there is no
 * dedicated download role.
 *
 * The datatable gives every `td` `overflow-x: auto`, which clips absolutely positioned children, so the
 * menu is placed with Popper's `fixed` strategy to escape the cell.
 */
export const DestockingMenu = ({ policy, onDownload, onClose }: Props) => (
  <Dropdown.Container>
    <Dropdown.Toggle
      bsPrefix='toggle'
      as='div'
      id={`destocking-actions-${policy.name}`}
    >
      <Icon icon='dots' size='xs' className='cursor-pointer' />
    </Dropdown.Toggle>
    <Dropdown.Menu
      renderOnMount
      popperConfig={{ strategy: 'fixed' }}
      className='dropdown-caret dropdown-menu-card dropdown-menu-end'
    >
      <PrivateComponent roles={[ROLES.ROLE_DESTOCKING_PRICING_POLICY_GET]}>
        <Dropdown.Item
          id={`download-destocking-${policy.name}`}
          onClick={onDownload}
        >
          <Icon icon='download' size='xxs' className='me-2' />
          <Trans i18nKey='app.common.buttons.download' />
        </Dropdown.Item>
      </PrivateComponent>
      <PrivateComponent roles={[ROLES.ROLE_DESTOCKING_PRICING_POLICY_CLOSE]}>
        <Dropdown.Item
          id={`close-destocking-${policy.name}`}
          onClick={onClose}
          disabled={policy.status !== DESTOCKING_POLICY_STATUS.ACTIVE}
        >
          <Icon icon='close-circle' size='xxs' className='me-2' />
          <Trans i18nKey='app.common.button.enclose' />
        </Dropdown.Item>
      </PrivateComponent>
    </Dropdown.Menu>
  </Dropdown.Container>
);
